import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useAuth } from '@/contexts/AuthContext';
import { orderService } from '@/services/order';
import type { Order } from '@/types';
import { reviewService } from '@/services/review';
import { useToast } from '@/components/common/Toast';

// Desktop components following SRP
import { ReviewHeader } from '@/components/desktop/review/ReviewHeader';
import { ReviewForm } from '@/components/desktop/review/ReviewForm';

export function DesktopReviewWritePage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();

  // 모바일 로직 재사용 - 상태 관리
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!orderId) {
      showToast('잘못된 접근입니다');
      navigate('/buyer/orders');
      return;
    }

    fetchOrderData();
  }, [user, orderId, navigate]);

  // 모바일 로직 재사용 - 주문 데이터 가져오기
  const fetchOrderData = async () => {
    if (!user || !orderId) return;

    setIsLoading(true);
    try {
      const orderData = await orderService.getOrderById(orderId);
      
      if (!orderData) {
        showToast('주문을 찾을 수 없습니다');
        navigate('/buyer/orders');
        return;
      }

      // 주문 완료된 상태인지 확인
      if (orderData.status !== 'COMPLETED') {
        showToast('완료된 주문만 리뷰 작성이 가능합니다');
        navigate('/buyer/orders');
        return;
      }

      // 이미 리뷰를 작성했는지 확인
      const existingReview = await reviewService.getReviewByOrderId(orderId, user.id);
      if (existingReview) {
        showToast('이미 리뷰를 작성한 주문입니다');
        navigate('/buyer/orders');
        return;
      }

      setOrder(orderData);
    } catch (error) {
      console.error('Error fetching order:', error);
      showToast('주문 정보를 불러올 수 없습니다');
      navigate('/buyer/orders');
    } finally {
      setIsLoading(false);
    }
  };

  // 모바일 로직 재사용 - 리뷰 제출
  const handleSubmit = async () => {
    if (!user || !order || !orderId) return;

    if (!comment.trim()) {
      showToast('리뷰 내용을 입력해주세요');
      return;
    }

    if (comment.trim().length < 5) {
      showToast('리뷰는 5자 이상 작성해주세요');
      return;
    }

    setIsSubmitting(true);
    try {
      const reviewData = {
        order_id: orderId,
        restaurant_id: order.restaurant_id,
        rating,
        comment: comment.trim(),
        is_anonymous: isAnonymous,
        images: photos
      };

      const result = await reviewService.createReview(user.id, reviewData);
      
      if (result.success) {
        showToast('리뷰가 등록되었습니다');
        setTimeout(() => {
          navigate('/buyer/orders', { replace: true });
        }, 1500);
      } else {
        showToast(result.error || '리뷰 등록에 실패했습니다');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      showToast('리뷰 등록 중 오류가 발생했습니다');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <DesktopLayout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-gray-200 px-10 py-12">
          
          {/* Header Section - SRP: 리뷰 헤더와 주문 정보 표시만 담당 */}
          <ReviewHeader 
            order={order}
            className="mb-8"
          />
          
          {/* Form Section - SRP: 리뷰 입력 폼만 담당 */}
          <ReviewForm
            rating={rating}
            comment={comment}
            photos={photos}
            isAnonymous={isAnonymous}
            isSubmitting={isSubmitting}
            onRatingChange={setRating}
            onCommentChange={setComment}
            onPhotosChange={setPhotos}
            onAnonymousToggle={setIsAnonymous}
            onSubmit={handleSubmit}
          />

          {/* 뒤로가기 버튼 */}
          <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate('/buyer/orders')}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 text-[14px] transition-colors"
              disabled={isSubmitting}
            >
              주문 목록으로 돌아가기
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopLayout>
  );
}
